import { ChangeDetectionStrategy, Component, computed, ResourceRef, Signal, signal, WritableSignal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { PageHeaderComponent } from '@libs/common/components';
import { LastOrderedProductModel, SecondLevelLastOrderedProductsNode, TopLevelLastOrderedProductsNode } from '@libs/shop/models';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { catchError, defer, finalize, throwError } from 'rxjs';
import { FlashMessageModule, SpinnerOverlayService, FlashMessageService } from '@libs/shared/common';
import { LastOrderedProductsTableComponent } from '../../last-ordered-products/last-ordered-products-table.component';
import { LastOrderedProductsRepositoryService } from '../../services';

@Component({
  selector: 'ui-last-ordered-products-table-list',
  templateUrl: './last-ordered-products-table-list.component.html',
  imports: [
    MatExpansionModule,
    LastOrderedProductsTableComponent,
    FlashMessageModule,
    NgxSkeletonLoaderModule,
    PageHeaderComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LastOrderedProductsTableListComponent {
  public productsResource: ResourceRef<LastOrderedProductModel[] | undefined> = rxResource({
    stream: () => {
      return defer(() => {
        this.spinnerOverlayService.showLoader();

        return this.lastOrderedProductsRepositoryService.getAll().pipe(catchError((err: unknown) => {
          this.messageService.showMessage('errorLoadList');

          return throwError(() => err);
        }),
        finalize(() => void this.spinnerOverlayService.hideLoader()));
      });
    },
  });

  public nodes: Signal<TopLevelLastOrderedProductsNode[]> = computed(() => this.createNodes(this.productsResource.hasValue() ? this.productsResource.value() : undefined));

  public expandedCategory: WritableSignal<string | undefined> = signal(undefined);

  public constructor(
    private readonly lastOrderedProductsRepositoryService: LastOrderedProductsRepositoryService,
    private readonly spinnerOverlayService: SpinnerOverlayService,
    private readonly messageService: FlashMessageService,
  ) {
  }

  public setExpandedCategory(expanded: boolean, category: string): void {
    if (expanded) {
      this.expandedCategory.set(category);
    } else if (category === this.expandedCategory()) {
      this.expandedCategory.set(undefined);
    }
  }

  private createNodes(products?: LastOrderedProductModel[]): TopLevelLastOrderedProductsNode[] {
    const nodes: TopLevelLastOrderedProductsNode[] = [];

    if (!products) {
      return nodes;
    }

    // Create nodes from products
    const noCategoryId: string = 'Produkte ohne Kategorie';
    const topLevelNodeMap: Record<string, TopLevelLastOrderedProductsNode> = {};
    const secondLevelNodeMap: Record<string, SecondLevelLastOrderedProductsNode> = {};

    for (const product of products) {
      // Create top level node if it doesn't exist
      const topLevelId: string = product.categories.topLevel ?? noCategoryId;

      let topLevelNode: TopLevelLastOrderedProductsNode | undefined = topLevelNodeMap[topLevelId];

      if (!topLevelNode) {
        topLevelNode = {
          id: topLevelId,
          label: topLevelId,
          count: 0,
          children: [],
        };

        topLevelNodeMap[topLevelId] = topLevelNode;

        if (topLevelId !== noCategoryId) {
          nodes.push(topLevelNode);
        }
      }

      // Create second level node if it doesn't exist
      const secondLevelId: string = product.categories.secondLevel ?? noCategoryId;
      const secondLevelKey: string = `${topLevelId}-${secondLevelId}`;

      let secondLevelNode: SecondLevelLastOrderedProductsNode | undefined = secondLevelNodeMap[secondLevelKey];

      if (!secondLevelNode) {
        secondLevelNode = {
          id: secondLevelId,
          label: secondLevelId,
          items: [],
        };

        secondLevelNodeMap[secondLevelKey] = secondLevelNode;

        if (secondLevelId !== noCategoryId) {
          topLevelNode.children.push(secondLevelNode);
        }
      }

      // Add product to nodes
      topLevelNode.count += 1;
      secondLevelNode.items.push(product);
    }

    // Add products without category
    /* istanbul ignore else */
    if (topLevelNodeMap[noCategoryId]) {
      nodes.push(topLevelNodeMap[noCategoryId]);
    }

    nodes.forEach(node => {
      const secondLevelNoCategoryKey: string = `${node.id}-${noCategoryId}`;

      if (secondLevelNodeMap[secondLevelNoCategoryKey]) {
        node.children.push(secondLevelNodeMap[secondLevelNoCategoryKey]);
      }
    });

    return nodes;
  }
}
